/**
 * Inventory Audit Data Table — renders vehicles for the currently-selected
 * run + sheet.
 *
 * Column widths come from backend config (authoritative), with min/max
 * constraints also from backend. User-resized widths override config and
 * live in AuditContext (reset on sheet change; persistence is a future
 * polish commit).
 *
 * NEVER LOSE DATA: truncated cells show full value on hover via title
 * attribute. User can also drag any column's right edge to resize.
 *
 * Features:
 *  - Pinned columns sticky on horizontal scroll
 *  - Zebra striping (standard for main sheets, VIN-group for Dealer Duplicates)
 *  - Cell formatting: null → em-dash, VIN monospace, age_days with commas
 *  - Title-attribute tooltips on every cell (browser-native hover)
 *  - Drag-to-resize on every column's right edge
 *  - Table stretches to 100% of container width
 *  - Loading / empty / error states
 */
import React, { useCallback, useMemo, useState } from "react"

import { AuditColumnConfig, VehicleResponse } from "../../api/audit"
import { useAudit } from "../../context/AuditContext"
import { THEME } from "../../config/theme"
import ProblemChip from "./ProblemChip"
import VendorMatrix from "./VendorMatrix"

// Rough conversion from Excel character units to pixels.
const EXCEL_CHAR_TO_PX = 7
// Absolute minimum column width to prevent columns from disappearing entirely.
const ABSOLUTE_MIN_WIDTH_PX = 40

function computeColumnWidth(
  col: AuditColumnConfig,
  resizedOverride: number | undefined,
): number {
  if (resizedOverride !== undefined) {
    return Math.max(ABSOLUTE_MIN_WIDTH_PX, resizedOverride)
  }
  const target = col.width * EXCEL_CHAR_TO_PX
  const min = col.min_width !== null ? col.min_width * EXCEL_CHAR_TO_PX : 0
  const max = col.max_width !== null ? col.max_width * EXCEL_CHAR_TO_PX : Infinity
  return Math.max(ABSOLUTE_MIN_WIDTH_PX, Math.max(min, Math.min(target, max)))
}

function computePinnedOffsets(
  columns: AuditColumnConfig[],
  widthFor: (col: AuditColumnConfig) => number,
): number[] {
  const offsets: number[] = []
  let running = 0
  for (const col of columns) {
    if (col.pinned) {
      offsets.push(running)
      running += widthFor(col)
    } else {
      offsets.push(0)
    }
  }
  return offsets
}

function formatCellValue(field: string, value: unknown): string {
  if (value === null || value === undefined || value === "") {
    return "\u2014"
  }
  if (field === "age_days" && typeof value === "number") {
    return value.toLocaleString()
  }
  return String(value)
}

function computeDealerDupZebra(vehicles: VehicleResponse[]): string[] {
  const colors: string[] = []
  let currentVin: string | null = null
  let useDark = false
  for (const v of vehicles) {
    const vin = v.vin ?? ""
    if (vin !== currentVin) {
      useDark = !useDark
      currentVin = vin
    }
    colors.push(useDark ? THEME.auditTable.zebraDealerDup : THEME.auditTable.zebraLight)
  }
  return colors
}

// Resize handle — straddles the right border of a column header.
// Uses absolute drag math (startX + startWidth) for smooth, drift-free sizing.
function ResizeHandle({
  onStart,
  onResize,
}: {
  onStart: () => number
  onResize: (totalDeltaX: number, startWidth: number) => void
}) {
  const [isHovering, setIsHovering] = useState(false)
  const [isDragging, setIsDragging] = useState(false)

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      const startX = e.clientX
      const startWidth = onStart()
      setIsDragging(true)

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const totalDelta = moveEvent.clientX - startX
        onResize(totalDelta, startWidth)
      }

      const handleMouseUp = () => {
        setIsDragging(false)
        document.removeEventListener("mousemove", handleMouseMove)
        document.removeEventListener("mouseup", handleMouseUp)
        document.body.style.cursor = ""
        document.body.style.userSelect = ""
      }

      document.addEventListener("mousemove", handleMouseMove)
      document.addEventListener("mouseup", handleMouseUp)
      document.body.style.cursor = "col-resize"
      document.body.style.userSelect = "none"
    },
    [onStart, onResize],
  )

  const isActive = isHovering || isDragging

  return (
    <div
      onMouseDown={handleMouseDown}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      style={{
        position: "absolute",
        top: 0,
        right: -5,
        width: 10,
        height: "100%",
        cursor: "col-resize",
        userSelect: "none",
        zIndex: 4,
        display: "flex",
        justifyContent: "center",
      }}
      title="Drag to resize column"
    >
      <div
        style={{
          width: isActive ? 2 : 0,
          height: "100%",
          backgroundColor: THEME.colors.navy,
          transition: "width 0.1s ease",
        }}
      />
    </div>
  )
}

export default function AuditTable() {
  const {
    currentSheet,
    vehicles,
    vehiclesLoading,
    vehiclesError,
    columnConfig,
    columnConfigLoading,
    columnConfigError,
    totalMatching,
    resizedWidths,
    setColumnWidth,
    sort,
    cycleSort,
  } = useAudit()

  const widthFor = useCallback(
    (col: AuditColumnConfig) =>
      computeColumnWidth(col, resizedWidths[col.field]),
    [resizedWidths],
  )

  const pinnedOffsets = useMemo(
    () => computePinnedOffsets(columnConfig, widthFor),
    [columnConfig, widthFor],
  )

  const lastPinnedIndex = useMemo(() => {
    let idx = -1
    columnConfig.forEach((col, i) => {
      if (col.pinned) idx = i
    })
    return idx
  }, [columnConfig])

  const ddZebraColors = useMemo(() => {
    if (currentSheet !== "dealer_duplicates") return null
    return computeDealerDupZebra(vehicles)
  }, [currentSheet, vehicles])

  const isLoading = vehiclesLoading || columnConfigLoading
  const error = vehiclesError || columnConfigError

  const headerCellStyle = (
    col: AuditColumnConfig,
    idx: number,
  ): React.CSSProperties => {
    const width = widthFor(col)
    const base: React.CSSProperties = {
      width,
      minWidth: width,
      maxWidth: width,
      padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
      fontSize: 12,
      fontWeight: 500,
      letterSpacing: "0.5px",
      textTransform: "uppercase",
      color: THEME.colors.white,
      textAlign: col.center_aligned ? "center" : "left",
      backgroundColor: THEME.auditTable.headerBackground,
      borderRight: "1px solid rgba(255, 255, 255, 0.1)",
      borderBottom: `1px solid ${THEME.auditTable.borderColor}`,
      boxSizing: "border-box",
      position: "sticky",
      top: 0,
      zIndex: col.pinned ? 3 : 2,
    }
    if (col.pinned) {
      base.left = pinnedOffsets[idx]
      if (idx === lastPinnedIndex) {
        base.boxShadow = `2px 0 4px ${THEME.auditTable.pinnedShadow}`
      }
    }
    return base
  }

  const dataCellStyle = (
    col: AuditColumnConfig,
    idx: number,
    rowBackground: string,
  ): React.CSSProperties => {
    const width = widthFor(col)
    const base: React.CSSProperties = {
      width,
      minWidth: width,
      maxWidth: width,
      padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
      fontSize: 12,
      color: THEME.colors.neutral.light.textPrimary,
      textAlign: col.center_aligned ? "center" : "left",
      backgroundColor: rowBackground,
      borderRight: `1px solid ${THEME.auditTable.borderColor}`,
      borderBottom: `1px solid ${THEME.auditTable.borderColor}`,
      boxSizing: "border-box",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      fontFamily:
        col.field === "vin"
          ? 'ui-monospace, "SF Mono", Menlo, Consolas, monospace'
          : "inherit",
    }
    if (col.pinned) {
      base.position = "sticky"
      base.left = pinnedOffsets[idx]
      base.zIndex = 1
      if (idx === lastPinnedIndex) {
        base.boxShadow = `2px 0 4px ${THEME.auditTable.pinnedShadow}`
      }
    }
    return base
  }

  const rowBackgroundColor = (rowIdx: number): string => {
    if (ddZebraColors) {
      return ddZebraColors[rowIdx] ?? THEME.auditTable.zebraLight
    }
    return rowIdx % 2 === 0 ? THEME.auditTable.zebraLight : THEME.auditTable.zebraMain
  }

  const containerStyle: React.CSSProperties = {
    width: "100%",
    overflowX: "auto",
    overflowY: "auto",
    maxHeight: "calc(100vh - 320px)",
    border: `1px solid ${THEME.auditTable.borderColor}`,
    borderRadius: THEME.borderRadius.md,
    backgroundColor: THEME.colors.neutral.light.surface,
  }

  const tableStyle: React.CSSProperties = {
    borderCollapse: "separate",
    borderSpacing: 0,
    tableLayout: "fixed",
    // Table stretches to at least container width; columns may push it wider.
    width: "100%",
    minWidth: "max-content",
  }

  if (isLoading && vehicles.length === 0) {
    return (
      <div
        style={{
          padding: THEME.spacing.xl,
          textAlign: "center",
          color: THEME.colors.neutral.light.textSecondary,
          backgroundColor: THEME.colors.neutral.light.background,
          borderRadius: THEME.borderRadius.md,
          border: `1px solid ${THEME.auditTable.borderColor}`,
        }}
      >
        Loading vehicles…
      </div>
    )
  }

  if (error) {
    return (
      <div
        style={{
          padding: THEME.spacing.md,
          color: THEME.colors.semantic.danger,
          backgroundColor: "rgba(220, 38, 38, 0.08)",
          borderRadius: THEME.borderRadius.md,
          border: "0.5px solid rgba(220, 38, 38, 0.3)",
        }}
      >
        Failed to load: {error}
      </div>
    )
  }

  if (columnConfig.length === 0) {
    return (
      <div
        style={{
          padding: THEME.spacing.xl,
          color: THEME.colors.neutral.light.textSecondary,
          textAlign: "center",
        }}
      >
        No column configuration available.
      </div>
    )
  }

  if (vehicles.length === 0) {
    return (
      <div
        style={{
          padding: THEME.spacing.xl,
          textAlign: "center",
          color: THEME.colors.neutral.light.textSecondary,
          backgroundColor: THEME.colors.neutral.light.background,
          borderRadius: THEME.borderRadius.md,
          border: `1px solid ${THEME.auditTable.borderColor}`,
        }}
      >
        No vehicles on this sheet for the selected run.
      </div>
    )
  }

  return (
    <>
      <div
        style={{
          fontSize: 13,
          color: THEME.colors.neutral.light.textSecondary,
          marginBottom: THEME.spacing.sm,
        }}
      >
        Showing {vehicles.length.toLocaleString()} of {totalMatching.toLocaleString()} vehicles
      </div>
      <div style={containerStyle}>
        <table style={tableStyle}>
          <thead>
            <tr>
              {columnConfig.map((col, idx) => (
                <th
                  key={col.field}
                  style={headerCellStyle(col, idx)}
                  onClick={col.sortable ? () => cycleSort(col.field) : undefined}
                  title={col.sortable ? "Click to sort" : undefined}
                >
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      cursor: col.sortable ? "pointer" : "default",
                      userSelect: "none",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "100%",
                    }}
                  >
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {col.label}
                    </span>
                    {col.sortable && sort?.field === col.field && (
                      <span
                        style={{
                          fontSize: 10,
                          color: THEME.colors.white,
                          flexShrink: 0,
                        }}
                      >
                        {sort.direction === "asc" ? "▲" : "▼"}
                      </span>
                    )}
                  </div>
                  <ResizeHandle
                    onStart={() => widthFor(col)}
                    onResize={(absoluteDelta, startWidth) =>
                      setColumnWidth(col.field, startWidth + absoluteDelta)
                    }
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {vehicles.map((vehicle, rowIdx) => {
              const bg = rowBackgroundColor(rowIdx)
              return (
                <tr key={vehicle.id}>
                  {columnConfig.map((col, colIdx) => {
                    const rawValue = (vehicle as unknown as Record<string, unknown>)[col.field]
                    const display = formatCellValue(col.field, rawValue)
                    // Audit-remodel Phase 2: runs that carry a problem_type slug
                    // render the named chip + worded vendor matrix instead of the
                    // legacy emoji string; older runs keep the plain text.
                    const problemSlug =
                      col.field === "flag_type" ? vehicle.problem_type : null
                    return (
                      <td
                        key={col.field}
                        style={dataCellStyle(col, colIdx, bg)}
                        title={problemSlug ? undefined : display}
                      >
                        {problemSlug ? (
                          <span
                            style={{
                              display: "inline-flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                              gap: 2,
                              padding: "2px 0",
                            }}
                          >
                            <ProblemChip slug={problemSlug} detail={vehicle.problem_detail} />
                            <VendorMatrix
                              tkBucket={vehicle.tk_bucket}
                              rrBucket={vehicle.rr_bucket}
                              hnBucket={vehicle.hn_bucket}
                              millBucket={vehicle.mill_bucket}
                              tkRaw={vehicle.tekion_status}
                              rrRaw={vehicle.rr_step}
                              millRaw={vehicle.rr_recon_step}
                              hnRaw={vehicle.homenet_state}
                              stockType={vehicle.vehicle_type}
                            />
                          </span>
                        ) : (
                          display
                        )}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </>
  )
}
